import { useState } from 'react';
import { purchaseCredits, subscribe, sendCAPIEvent } from '../lib/api';
import posthog from '../lib/posthog';
import { trackMetaEvent } from '../lib/meta';
import { getAdAttribution } from '../lib/adAttribution';
import { buildCheckoutReturnUrl } from '../lib/checkoutReturnUrls';
import './BuyCreditsModal.css';

const TIERS = [
  { cents: 1000, label: '$10' },
  { cents: 2500, label: '$25' },
  { cents: 5000, label: '$50' },
  { cents: 10000, label: '$100' },
];

const MIN_CENTS = 500;
const MAX_CENTS = 10_000_000;

export default function BuyCreditsModal({
  isOpen,
  onClose,
  variant = 'default',
  hasSubscription = false,
  checkoutReturnUrl = null,
}) {
  const [selectedCents, setSelectedCents] = useState(2500);
  const [customValue, setCustomValue] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscribing, setSubscribing] = useState(null);

  if (!isOpen) return null;

  const effectiveCents = isCustom
    ? Math.round(parseFloat(customValue || '0') * 100)
    : selectedCents;

  const isValid = effectiveCents >= MIN_CENTS && effectiveCents <= MAX_CENTS;

  function handleTierClick(cents) {
    setSelectedCents(cents);
    setIsCustom(false);
    setCustomValue('');
    setError(null);
  }

  function handleCustomChange(e) {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    setCustomValue(val);
    setIsCustom(true);
    setError(null);
  }

  async function handlePurchase() {
    if (!isValid) return;
    setLoading(true);
    setError(null);
    try {
      const returnUrl = checkoutReturnUrl || window.location.href.split('?')[0];
      const attribution = getAdAttribution();
      const eventId = trackMetaEvent('InitiateCheckout', {
        value: effectiveCents / 100,
        currency: 'USD',
        content_type: 'credits',
        num_items: 1,
      });
      sendCAPIEvent('InitiateCheckout', eventId, {
        value: effectiveCents / 100,
        currency: 'USD',
        num_items: 1,
      }, attribution).catch(() => {});
      const { fbc, fbp } = attribution;
      const result = await purchaseCredits(
        effectiveCents,
        buildCheckoutReturnUrl(returnUrl, 'credits', 'success', true),
        buildCheckoutReturnUrl(returnUrl, 'credits', 'cancelled'),
        { eventId, fbc, fbp },
      );
      if (result.checkoutUrl) {
        posthog.capture('credits checkout started', {
          amount_cents: effectiveCents,
          variant,
        });
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      setError('Failed to start checkout');
      setLoading(false);
    }
  }

  async function handleSubscribe(tier) {
    setSubscribing(tier);
    try {
      const tierPrice = tier === 'studio' ? 200 : 20;
      const attribution = getAdAttribution();
      const subEventId = trackMetaEvent('InitiateCheckout', {
        value: tierPrice,
        currency: 'USD',
        content_type: 'subscription',
        num_items: 1,
      });
      sendCAPIEvent('InitiateCheckout', subEventId, {
        value: tierPrice,
        currency: 'USD',
        num_items: 1,
      }, attribution).catch(() => {});
      const successUrl = checkoutReturnUrl
        ? buildCheckoutReturnUrl(checkoutReturnUrl, 'subscription', 'success', true)
        : `${window.location.origin}/account?subscription=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = checkoutReturnUrl
        ? buildCheckoutReturnUrl(checkoutReturnUrl, 'subscription', 'cancelled')
        : window.location.href.split('?')[0];
      const result = await subscribe(tier, successUrl, cancelUrl, { eventId: subEventId, fbc: attribution.fbc, fbp: attribution.fbp });
      if (result.checkoutUrl) {
        posthog.capture('subscription checkout started', { tier });
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      setError('Failed to start checkout');
    } finally {
      setSubscribing(null);
    }
  }

  const isInsufficientVariant = variant === 'insufficient';
  const title = isInsufficientVariant ? 'Insufficient credits' : 'Buy Credits';
  const subtitle = isInsufficientVariant
    ? 'Top up your balance to continue generating videos.'
    : 'Credits are used to generate videos. Buy what you need.';

  return (
    <div className="buy-credits-backdrop" role="presentation" onClick={onClose}>
      <div
        className="buy-credits-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="buy-credits-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="buy-credits-header">
          <h2 id="buy-credits-title">{title}</h2>
          <p>{subtitle}</p>
        </header>

        {/* Subscription upsell — only shown to non-subscribers */}
        {!hasSubscription && (
          <div className="buy-credits-upsell">
            <div className="buy-credits-upsell__tag">Better value — subscribe instead</div>
            <div className="buy-credits-upsell__plans">
              <div className="buy-credits-upsell__plan buy-credits-upsell__plan--featured">
                <div className="buy-credits-upsell__plan-header">
                  <strong>Creator</strong>
                  <span>$20/mo</span>
                </div>
                <ul className="buy-credits-upsell__plan-features">
                  <li>$30/mo video allowance</li>
                  <li>Unlimited images</li>
                  <li>Unlimited voice</li>
                  <li>PAYG after allowance</li>
                </ul>
                <button
                  type="button"
                  className="buy-credits-upsell__btn"
                  disabled={!!subscribing}
                  onClick={() => handleSubscribe('creator')}
                >
                  {subscribing === 'creator' ? 'Loading...' : 'Subscribe to Creator'}
                </button>
              </div>
              <div className="buy-credits-upsell__plan">
                <div className="buy-credits-upsell__plan-header">
                  <strong>Studio</strong>
                  <span>$200/mo</span>
                </div>
                <ul className="buy-credits-upsell__plan-features">
                  <li>$500/mo video allowance</li>
                  <li>Unlimited images</li>
                  <li>Unlimited voice</li>
                  <li>Priority processing</li>
                </ul>
                <button
                  type="button"
                  className="buy-credits-upsell__btn buy-credits-upsell__btn--alt"
                  disabled={!!subscribing}
                  onClick={() => handleSubscribe('studio')}
                >
                  {subscribing === 'studio' ? 'Loading...' : 'Subscribe to Studio'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="buy-credits-tiers">
          {TIERS.map((tier) => (
            <button
              key={tier.cents}
              type="button"
              className={`buy-credits-tier${!isCustom && selectedCents === tier.cents ? ' selected' : ''}`}
              onClick={() => handleTierClick(tier.cents)}
            >
              <span className="buy-credits-tier-amount">{tier.label}</span>
              <span className="buy-credits-tier-label">credits</span>
            </button>
          ))}
        </div>

        <div className="buy-credits-custom">
          <span className="buy-credits-custom-label">Or enter a custom amount</span>
          <div className="buy-credits-custom-input-row">
            <span>$</span>
            <input
              type="text"
              inputMode="decimal"
              className="buy-credits-custom-input"
              placeholder="e.g. 75"
              value={customValue}
              onChange={handleCustomChange}
              onFocus={() => setIsCustom(true)}
            />
          </div>
        </div>

        {error && <p className="buy-credits-error">{error}</p>}

        <div className="buy-credits-actions">
          <button
            type="button"
            className="buy-credits-primary"
            disabled={!isValid || loading}
            onClick={handlePurchase}
          >
            {loading
              ? 'Redirecting...'
              : `Buy $${(effectiveCents / 100).toFixed(2)} in credits`}
          </button>
          <button type="button" className="buy-credits-secondary" onClick={onClose}>
            {isInsufficientVariant ? 'Cancel' : 'Maybe later'}
          </button>
        </div>
      </div>
    </div>
  );
}
